"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.RailgunTxidMerkletree = void 0;
const bn_js_1 = __importDefault(require("bn.js"));
const merkletree_types_1 = require("../models/merkletree-types");
const merkletree_1 = require("./merkletree");
const bytes_1 = require("../utils/bytes");
const utils_1 = require("../utils");
class RailgunTxidMerkletree extends merkletree_1.Merkletree {
    constructor(db, chain, merklerootValidator, isPOINode) {
        // For Txid merkletree on POI Nodes, we will calculate for every Single tree update, in order to capture its merkleroot.
        const commitmentProcessingGroupSize = isPOINode
            ? merkletree_types_1.CommitmentProcessingGroupSize.Single
            : merkletree_types_1.CommitmentProcessingGroupSize.XXXLarge;
        super(db, chain, merklerootValidator, commitmentProcessingGroupSize);
        // DO NOT MODIFY
        this.merkletreePrefix = 'railgun-transaction-ids';
        this.merkletreeType = 'Railgun Txid';
        this.shouldStoreMerkleroots = isPOINode;
    }
    /**
     * Wallet validates merkleroots against POI Nodes.
     */
    static async createForWallet(db, chain, merklerootValidator) {
        const isPOINode = false;
        const merkletree = new RailgunTxidMerkletree(db, chain, merklerootValidator, isPOINode);
        await merkletree.init();
        return merkletree;
    }
    /**
     * POI Node is the source of truth, so it will not validate merkleroots.
     * Instead, it will process every tree update individually, and store each in the database.
     */
    static async createForPOINode(db, chain) {
        // Assume all merkleroots are valid.
        const merklerootValidator = async () => true;
        // For Txid merkletree on POI Nodes, store all merkleroots.
        const isPOINode = true;
        const merkletree = new RailgunTxidMerkletree(db, chain, merklerootValidator, isPOINode);
        await merkletree.init();
        return merkletree;
    }
    /**
     * Gets Railgun Transaction data from txid tree.
     */
    async getRailgunTransaction(tree, index) {
        try {
            return await this.getData(tree, index);
        }
        catch (err) {
            return undefined;
        }
    }
    async getRailgunTxidCurrentMerkletreeData(railgunTxid) {
        const txidIndex = await this.getTxidIndexByRailgunTxid(railgunTxid);
        if (!(0, utils_1.isDefined)(txidIndex)) {
            throw new Error('txid index not found');
        }
        const { tree, index } = RailgunTxidMerkletree.getTreeAndIndexFromTxidIndex(txidIndex);
        const railgunTransaction = await this.getRailgunTransaction(tree, index);
        if (!(0, utils_1.isDefined)(railgunTransaction)) {
            throw new Error('railgun transaction not found');
        }
        const currentMerkleProofForTree = await this.getMerkleProof(tree, index);
        const currentIndex = await this.getLatestIndexForTree(tree);
        const currentTxidIndexForTree = RailgunTxidMerkletree.getTxidIndex(tree, currentIndex);
        return {
            railgunTransaction,
            currentMerkleProofForTree,
            currentTxidIndexForTree,
        };
    }
    // TODO: Remove after V3
    async getRailgunTxidsForCommitments(commitments) {
        const commitmentToTxid = {};
        await Promise.all(commitments.map(async (commitment) => {
            const railgunTxid = await this.getRailgunTxidByCommitment(commitment);
            commitmentToTxid[commitment] = railgunTxid;
        }));
        return commitmentToTxid;
    }
    async getLatestGraphID() {
        const { tree, index } = await this.getLatestTreeAndIndex();
        const railgunTransaction = await this.getRailgunTransaction(tree, index);
        return railgunTransaction?.graphID;
    }
    async queueRailgunTransactions(railgunTransactionsWithTxids, maxTxidIndex) {
        const { tree: latestTree, index: latestIndex } = await this.getLatestTreeAndIndex();
        let nextTree = latestTree;
        let nextIndex = latestIndex;
        for (let i = 0; i < railgunTransactionsWithTxids.length; i += 1) {
            const { tree, index } = RailgunTxidMerkletree.nextTreeAndIndex(nextTree, nextIndex);
            nextTree = tree;
            nextIndex = index;
            if (RailgunTxidMerkletree.isOutOfBounds(nextTree, nextIndex, maxTxidIndex)) {
                return;
            }
            const railgunTransactionWithTxid = railgunTransactionsWithTxids[i];
            // eslint-disable-next-line no-await-in-loop
            await this.queueLeaves(nextTree, nextIndex, [railgunTransactionWithTxid]);
            // eslint-disable-next-line no-await-in-loop
            await this.storeCommitmentsToRailgunTxid(railgunTransactionWithTxid);
            // eslint-disable-next-line no-await-in-loop
            await this.storeRailgunTxidIndexLookup(railgunTransactionWithTxid.hash, nextTree, nextIndex);
        }
    }
    static isOutOfBounds(tree, index, maxTxidIndex) {
        if (!(0, utils_1.isDefined)(maxTxidIndex)) {
            return false;
        }
        return RailgunTxidMerkletree.getTxidIndex(tree, index) > maxTxidIndex;
    }
    static nextTreeAndIndex(tree, index) {
        if (index + 1 >= merkletree_types_1.TREE_MAX_ITEMS) {
            return { tree: tree + 1, index: 0 };
        }
        return { tree, index: index + 1 };
    }
    async clearLeavesAfterTxidIndex(txidIndex) {
        // Lock for updates
        this.lockUpdates = true;
        // Remove any queued items
        this.writeQueue = [];
        // Clear cache
        this.sortedAllDataCache = undefined;
        const { tree, index } = RailgunTxidMerkletree.getTreeAndIndexFromTxidIndex(txidIndex);
        const { tree: latestTree, index: latestIndex } = await this.getLatestTreeAndIndex();
        for (let currentTree = tree; currentTree <= latestTree; currentTree += 1) {
            const startIndex = currentTree === tree ? index + 1 : 0;
            const max = currentTree === latestTree ? latestIndex : merkletree_types_1.TREE_MAX_ITEMS - 1;
            for (let currentIndex = startIndex; currentIndex <= max; currentIndex += 1) {
                // eslint-disable-next-line no-await-in-loop
                const railgunTransaction = await this.getRailgunTransaction(currentTree, currentIndex);
                if ((0, utils_1.isDefined)(railgunTransaction)) {
                    // eslint-disable-next-line no-await-in-loop
                    await this.db.del(this.getRailgunTxidLookupDBPath(railgunTransaction.hash));
                }
                // eslint-disable-next-line no-await-in-loop
                await this.db.del(this.getHistoricalMerklerootDBPath(currentTree, currentIndex));
                // eslint-disable-next-line no-await-in-loop
                await this.db.del(this.getDataDBPath(currentTree, currentIndex));
            }
            // eslint-disable-next-line no-await-in-loop
            await this.clearAllNodeHashes(currentTree);
        }
        for (let currentTree = tree; currentTree <= latestTree; currentTree += 1) {
            // eslint-disable-next-line no-await-in-loop
            await this.rebuildAndWriteTree(currentTree);
            // eslint-disable-next-line no-await-in-loop
            await this.resetTreeLength(currentTree);
            // eslint-disable-next-line no-await-in-loop
            await this.updateStoredMerkletreesMetadata(currentTree);
        }
        // Unlock updates
        this.lockUpdates = false;
    }
    async getCurrentTxidIndex() {
        const { tree, index } = await this.getLatestTreeAndIndex();
        return RailgunTxidMerkletree.getTxidIndex(tree, index);
    }
    static getTxidIndex(tree, index) {
        return tree * merkletree_types_1.TREE_MAX_ITEMS + index;
    }
    static getTreeAndIndexFromTxidIndex(txidIndex) {
        return {
            tree: Math.floor(txidIndex / merkletree_types_1.TREE_MAX_ITEMS),
            index: txidIndex % merkletree_types_1.TREE_MAX_ITEMS,
        };
    }
    // eslint-disable-next-line class-methods-use-this
    validRootCallback() {
        // Unused for Txid merkletree
        return Promise.resolve();
    }
    // eslint-disable-next-line class-methods-use-this
    invalidRootCallback() {
        // Unused for Txid merkletree
        return Promise.resolve();
    }
    getCommitmentLookupDBPath(commitment) {
        const commitmentLookupPrefix = (0, bytes_1.fromUTF8String)('commitment-lookup');
        return [...this.getChainDBPrefix(), commitmentLookupPrefix, commitment].map((el) => (0, bytes_1.formatToByteLength)(el, bytes_1.ByteLength.UINT_256));
    }
    async storeCommitmentsToRailgunTxid(railgunTransaction) {
        await Promise.all(railgunTransaction.commitments.map(async (commitment) => {
            await this.db.put(this.getCommitmentLookupDBPath(commitment), railgunTransaction.hash);
        }));
    }
    async getRailgunTxidByCommitment(commitment) {
        try {
            const railgunTxid = (await this.db.get(this.getCommitmentLookupDBPath(commitment)));
            return railgunTxid;
        }
        catch (err) {
            if (!(err instanceof Error)) {
                throw err;
            }
            return undefined;
        }
    }
    getRailgunTxidLookupDBPath(railgunTxid) {
        const railgunTxidPrefix = (0, bytes_1.fromUTF8String)('railgun-txid-lookup');
        return [...this.getChainDBPrefix(), railgunTxidPrefix, railgunTxid].map((el) => (0, bytes_1.formatToByteLength)(el, bytes_1.ByteLength.UINT_256));
    }
    async getTxidIndexByRailgunTxid(railgunTxid) {
        try {
            const txidIndexString = (await this.db.get(this.getRailgunTxidLookupDBPath(railgunTxid), 'utf8'));
            return Number(txidIndexString);
        }
        catch (err) {
            if (!(err instanceof Error)) {
                throw err;
            }
            return undefined;
        }
    }
    async getRailgunTransactionByTxid(railgunTxid) {
        try {
            const txidIndex = await this.getTxidIndexByRailgunTxid(railgunTxid);
            if (!(0, utils_1.isDefined)(txidIndex)) {
                throw new Error('Txid index not found');
            }
            const { tree, index } = RailgunTxidMerkletree.getTreeAndIndexFromTxidIndex(txidIndex);
            return await this.getRailgunTransaction(tree, index);
        }
        catch (err) {
            if (!(err instanceof Error)) {
                throw err;
            }
            return undefined;
        }
    }
    async storeRailgunTxidIndexLookup(railgunTxid, tree, index) {
        const txidIndex = RailgunTxidMerkletree.getTxidIndex(tree, index);
        await this.db.put(this.getRailgunTxidLookupDBPath(railgunTxid), txidIndex, 'utf8');
    }
    getHistoricalMerklerootDBPath(tree, index) {
        const merklerootPrefix = (0, bytes_1.fromUTF8String)('merkleroots');
        return [
            ...this.getChainDBPrefix(),
            merklerootPrefix,
            (0, bytes_1.hexlify)(new bn_js_1.default(tree)),
            (0, bytes_1.hexlify)(new bn_js_1.default(index)),
        ].map((el) => (0, bytes_1.formatToByteLength)(el, bytes_1.ByteLength.UINT_256));
    }
    async newLeafRootTrigger(tree, index, leaf, merkleroot) {
        if (this.shouldStoreMerkleroots) {
            await this.db.put(this.getHistoricalMerklerootDBPath(tree, index), merkleroot);
        }
    }
    async getHistoricalMerkleroot(tree, index) {
        try {
            const merkleroot = (await this.db.get(this.getHistoricalMerklerootDBPath(tree, index)));
            return merkleroot;
        }
        catch (err) {
            if (!(err instanceof Error)) {
                throw err;
            }
            return undefined;
        }
    }
    async getHistoricalMerklerootForTxidIndex(txidIndex) {
        const { tree, index } = RailgunTxidMerkletree.getTreeAndIndexFromTxidIndex(txidIndex);
        return this.getHistoricalMerkleroot(tree, index);
    }
}
exports.RailgunTxidMerkletree = RailgunTxidMerkletree;
